import React, { useEffect, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MuiTooltip from "@mui/material/Tooltip";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import { Doughnut } from "react-chartjs-2";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";

import {
  apiCallGetLocalAreaTrends,
  apiCallGetTimeOfDay,
} from "../../../Core/Apicall";
import { red } from "@mui/material/colors";

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

type FilterType = "last7days" | "last30days" | "last90days";

const FILTERS = [
  { key: "last7days", label: "Last 7 Days" },
  { key: "last30days", label: "Last 30 Days" },
  { key: "last90days", label: "Last 90 Days" },
];

export default function MarketIntelligence({ venueId }: { venueId: string }) {

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [timeOfDayData, setTimeOfDayData] = useState<any[]>([]);
  const [timeFilter, setTimeFilter] = useState("last90days");

  useEffect(() => {
    if (venueId) {
      fetchAnalytics();
    }
  }, [venueId]);

  useEffect(() => {
    if (venueId) {
      fetchTimeOfDay();
    }
  }, [venueId, timeFilter]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await apiCallGetLocalAreaTrends({ venueId });
      console.log("res", res);

      setData(res);
    } catch (error) {
      console.error("Analytics error", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeOfDay = async () => {
    try {
      const res = await apiCallGetTimeOfDay({
        venueId,
        filter: timeFilter,
      });

      setTimeOfDayData(res?.data || []);
    } catch (error) {
      console.error("Time of day error", error);
    }
  };

  const timeOfDayChart = timeOfDayData.map((item: any) => ({
    name: item.timeOfDay,
    stay: item.avgStayMinutes,
    guests: item.totalGuests,
    percentage: item.stayPercentage,
  }));

  const doughnutData = {
    labels: timeOfDayChart.map((d) => d.name),
    datasets: [
      {
        data: timeOfDayChart.map((d) => d.stay),
        backgroundColor: [
          "#e99b6f",
          "#cb8c4e",
          "#3c46d2",
          "#d9a3cf",
        ],
        borderWidth: 0,
        radius: "80%",
      },
    ],
  };

  const doughnutOptions: any = {
    cutout: "65%",
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1500,
      easing: "easeOutQuart",
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const data = timeOfDayChart[context.dataIndex];
            return `${data.name}: ${data.stay} min | Guests: ${data.guests}`;
          },
        },
      },
      legend: {
        position: "bottom",
      },
    },
  };

  const barChartData = timeOfDayChart.map((item) => ({
    name: item.name,
    percentage: item.percentage,
    guests: item.guests,
  }));

  const risingData = data?.risingData;
  const seasonalPatterns = data?.seasonalPatterns || [];

  const growthChartData = [
    {
      name: risingData?.previousPeriod || "Previous 2 Months",
      checkins: risingData?.previousTwoMonthsCheckIns,
    },
    {
      name: risingData?.lastPeriod || "Last 2 Months",
      checkins: risingData?.lastTwoMonthsCheckIns,
    },
  ];

  const seasonalChartData = seasonalPatterns.map((item: any) => ({
    year: item.year,
    highest: item.highMonth?.totalCheckIns,
    highestMonth: item.highMonth?.monthName,
    lowest: item.lowMonth?.totalCheckIns,
    lowestMonth: item.lowMonth?.monthName,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "12px",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
            border: "none",
          }}
        >
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "14px" }}>{label}</p>
          <hr style={{ border: "0.5px solid #E5E7EB", marginBottom: "8px" }} />

          {/* Highest Month Info */}
          <p style={{ color: "#F97316", fontSize: "13px", margin: "4px 0" }}>
            <strong>Highest check-ins:</strong> {data.highest} ( {data.highestMonth} )
          </p>

          {/* Lowest Month Info */}
          <p style={{ color: "#3B82F6", fontSize: "13px", margin: "4px 0" }}>
            <strong>Lowest check-ins:</strong> {data.lowest} ( {data.lowestMonth} )
          </p>
        </div>
      );
    }
    return null;
  };


  return (
    <div style={pageWrapper}>

      {loading ? (
        <p>Loading analytics...</p>
      ) : (
        <>
          {/* ================= Growth Trend ================= */}

          <div style={mainCard}>
            <div style={cardHeader}>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 style={{ margin: 0 }}>Venue Growth Trend</h3>

                <MuiTooltip
                  title="Monthly check-in comparison between previous and last two months"
                  arrow
                >
                  <InfoOutlinedIcon style={{ fontSize: 18, color: "#9CA3AF" }} />
                </MuiTooltip>
              </div>

            </div>

            <div style={kpiWrapper}>

              <div style={kpiCard}>
                <p style={kpiLabel}>{risingData?.previousPeriod || "Previous 2 Months"}</p>
                <h2 style={kpiValue}>
                  {risingData?.previousTwoMonthsCheckIns || 0}
                </h2>
              </div>

              <div style={kpiCard}>
                <p style={kpiLabel}>{risingData?.lastPeriod || "Last 2 Months"}</p>
                <h2 style={kpiValue}>
                  {risingData?.lastTwoMonthsCheckIns || 0}
                </h2>
              </div>

              <div style={kpiCard}>
                <p style={kpiLabel}>Growth</p>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

                  <h2 style={{ ...kpiValue, color: "#10B981" }}>
                    {risingData?.growthPercentage || 0}%
                  </h2>

                  {risingData?.trend === "Rising" ? (
                    <TrendingUpIcon style={{ color: "#10B981" }} />
                  ) : (
                    <TrendingDownIcon style={{ color: "#EF4444" }} />
                  )}

                </div>

                <span style={trendBadge(risingData?.trend)}>
                  {risingData?.trend}
                </span>

              </div>

            </div>

            <div style={{ height: 320 }}>

              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={growthChartData} barCategoryGap={90}>

                  <defs>
                    <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.9} />
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0.4} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />

                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 13, fill: "#6B7280" }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    tick={{ fontSize: 13, fill: "#6B7280" }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: "none",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                    }}
                  />

                  <Bar
                    dataKey="checkins"
                    fill="url(#growthGradient)"
                    radius={[10, 10, 0, 0]}
                    animationDuration={900}
                  />

                </BarChart>
              </ResponsiveContainer>

            </div>

          </div>

          {/* ================= Seasonal Patterns ================= */}

          <div style={mainCard}>

            <div style={cardHeader}>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 style={{ margin: 0 }}>Seasonal Patterns</h3>

                <MuiTooltip
                  title="Highest and lowest performing months each year"
                  arrow
                >
                  <InfoOutlinedIcon style={{ fontSize: 18, color: "#9CA3AF" }} />
                </MuiTooltip>
              </div>

            </div>

            <div style={{ height: 350 }}>

              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={seasonalChartData} barGap={10}>
                  <Tooltip content={<CustomTooltip />} />

                  <defs>
                    <linearGradient id="highGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F97316" stopOpacity={0.9} />
                      <stop offset="95%" stopColor="#FDBA74" stopOpacity={0.5} />
                    </linearGradient>

                    <linearGradient id="lowGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.9} />
                      <stop offset="95%" stopColor="#93C5FD" stopOpacity={0.5} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />

                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 13, fill: "#6B7280" }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    tick={{ fontSize: 13, fill: "#6B7280" }}
                    axisLine={false}
                    tickLine={false}
                  />


                  <Legend
                    wrapperStyle={{
                      paddingTop: 20,
                    }}
                  />

                  <Bar
                    dataKey="highest"
                    name="Highest Month"
                    fill="url(#highGradient)"
                    radius={[8, 8, 0, 0]}
                    animationDuration={800}
                  />

                  <Bar
                    dataKey="lowest"
                    name="Lowest Month"
                    fill="url(#lowGradient)"
                    radius={[8, 8, 0, 0]}
                    animationDuration={800}
                  />

                </BarChart>
              </ResponsiveContainer>

            </div>

          </div>

          {/* ================= Time Of Day ================= */}

          <div style={mainCard}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 10px", marginBottom: 20 }}>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 style={{ margin: 0 }}>Time Of Day Engagement</h3>

                <MuiTooltip
                  title="Shows how long guests stay at different times of the day."
                  arrow
                >
                  <InfoOutlinedIcon style={{ fontSize: 18, color: "#9CA3AF" }} />
                </MuiTooltip>
              </div>

              {/* FILTERS */}
              <div style={filterWrapper}>
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setTimeFilter(f.key as FilterType)}
                    style={{
                      ...filterButton,
                      ...(timeFilter === f.key ? activeFilter : {}),
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

            </div>

            <div style={{ display: "flex", gap: 40, alignItems: "center" }}>

              {/* Doughnut Chart */}
              <div style={{ position: "relative", width: "40%", height: 300 }}>
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </div>

              {/* Bar Chart */}
              <div style={{ width: "65%", height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barChartData}>

                    <defs>
                      <linearGradient id="stayGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.9} />
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0.4} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />

                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 13, fill: "#6B7280" }}
                      axisLine={false}
                      tickLine={false}
                    />

                    <YAxis
                      tick={{ fontSize: 13, fill: "#6B7280" }}
                      axisLine={false}
                      tickLine={false}
                      unit="%"
                    />

                    <Tooltip
                      cursor={{ fill: 'transparent' }} 
                      contentStyle={{
                        borderRadius: 12,
                        border: "none",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                        padding: "10px 14px",
                      }}
                      labelStyle={{
                        fontWeight: "600",
                        color: "#111827",
                        marginBottom: "4px"
                      }}
                      itemStyle={{
                        padding: "0",
                        color: "#374151"
                      }}
                      formatter={(value: any, name: any, props: any) => {
                        return [
                          `Guests : ${props.payload.guests} || Stay : ${value}%`
                        ];
                      }}
                    />

                    <Legend />

                    <Bar
                      dataKey="percentage"
                      name="Stay Percentage"
                      fill="url(#stayGradient)"
                      radius={[10, 10, 0, 0]}
                    />

                  </BarChart>
                </ResponsiveContainer>
              </div>

            </div>

          </div>
        </>
      )}

    </div>
  );
}

const pageWrapper: React.CSSProperties = {
  padding: 40,
  background: "#F3F4F6",
  minHeight: "100vh",
};

const mainCard: React.CSSProperties = {
  marginTop: 30,
  background: "#ffffff",
  borderRadius: 20,
  padding: 35,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
};

const cardHeader: React.CSSProperties = {
  marginBottom: 25,
};

const kpiWrapper: React.CSSProperties = {
  display: "flex",
  gap: 20,
  marginBottom: 30,
  flexWrap: "wrap",
};

const kpiCard: React.CSSProperties = {
  flex: 1,
  minWidth: 180,
  background: "#F9FAFB",
  borderRadius: 14,
  padding: 18,
  border: "1px solid #E5E7EB",
};

const kpiLabel: React.CSSProperties = {
  fontSize: 13,
  color: "#6B7280",
  marginBottom: 6,
};

const kpiValue: React.CSSProperties = {
  margin: 0,
  fontSize: 26,
  fontWeight: 600,
};

const trendBadge = (trend: string) => ({
  display: "inline-block",
  marginTop: 6,
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: 8,
  background: trend === "Rising" ? "#DCFCE7" : "#FEE2E2",
  color: trend === "Rising" ? "#16A34A" : "#DC2626",
});

const filterWrapper: React.CSSProperties = {
  display: "flex",
  gap: 12,
  marginTop: 25,
  justifyContent: "flex-end",
};

const filterButton: React.CSSProperties = {
  padding: "8px 18px",
  borderRadius: 25,
  border: "1px solid #E5E7EB",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
};

const activeFilter: React.CSSProperties = {
  background: "linear-gradient(135deg, #3B82F6, #2563EB)",
  color: "#fff",
  border: "none",
};